import React, { memo } from "react";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import { ContractRentStatusHistory } from "services/api/fleet/vehicles/contracts/types";
import { ContractRentStatusId } from "services/api/fleet/vehicles/contracts/consts";

interface ContractStatusToolbarProps {
  statusHistory: ContractRentStatusHistory[];
  onToggle: (statusId: string, checked: boolean) => void;
  disabled?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  [ContractRentStatusId.Draft]: "Predložak",
  [ContractRentStatusId.Created]: "Kreiran",
  [ContractRentStatusId.Sent]: "Poslan",
  [ContractRentStatusId.Signed]: "Potpisan",
  [ContractRentStatusId.Invoiced]: "Fakturiran",
};

const STATUS_ORDER = [
  ContractRentStatusId.Draft,
  ContractRentStatusId.Created,
  ContractRentStatusId.Sent,
  ContractRentStatusId.Signed,
  ContractRentStatusId.Invoiced,
];

const LOCKED_STATUSES: string[] = [ContractRentStatusId.Draft, ContractRentStatusId.Created];

const ContractStatusToolbar: React.FC<ContractStatusToolbarProps> = ({
  statusHistory,
  onToggle,
  disabled = false,
}) => {
  const orderedStatuses: ContractRentStatusHistory[] = STATUS_ORDER.map((id) => {
    const existing = statusHistory.find((h) => h.contractRentStatusId === id);
    return existing ?? {
      id: id,
      contractRentStatusId: id,
      statusName: STATUS_LABELS[id] ?? id,
      statusColor: null,
      checked: false,
      checkedAt: null,
    };
  });

  const lastCheckedIndex = orderedStatuses.reduce(
    (acc, s, i) => (s.checked ? i : acc),
    -1
  );

  return (
    <div className="flex border border-slate-200 rounded-sm overflow-hidden mb-4">
      {orderedStatuses.map((status, index) => {
        const isChecked = status.checked;
        const isCurrent = index === lastCheckedIndex + 1;
        const isDone = isChecked;
        const isLastDone = index === lastCheckedIndex;
        const isPast = isDone && !isLastDone;
        const isLocked = LOCKED_STATUSES.includes(status.contractRentStatusId);
        const createdIndex = orderedStatuses.findIndex((s) => s.contractRentStatusId === ContractRentStatusId.Created);
        const isCreatedChecked = createdIndex >= 0 && orderedStatuses[createdIndex].checked;
        const isDisabled = disabled || isLocked || (!isCreatedChecked && !isLocked);

        return (
          <button
            key={status.contractRentStatusId}
            type="button"
            disabled={isDisabled}
            onClick={() => onToggle(status.contractRentStatusId, !status.checked)}
            className={classNames(
              "flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm transition-all duration-200 border-r last:border-r-0",
              isDone
                ? "bg-green-100 text-green-800 border-b-2 border-b-green-600 font-semibold"
                : isCurrent
                ? "bg-sky-50 text-sky-700 border-slate-200 font-medium"
                : "bg-white text-slate-400 border-slate-200 font-medium",
              !isDisabled && "cursor-pointer hover:opacity-80"
            )}
          >
            {isDone && (
              <ReactSVG
                src="/media/icons/check.svg"
                className="w-4 h-4 stroke-green-700"
              />
            )}
            <span>{status.statusName}</span>
          </button>
        );
      })}
    </div>
  );
};

export default memo(ContractStatusToolbar);
